import React, { useState, useRef, useEffect, useImperativeHandle } from 'react';
import { openDeckPage } from '../../openDeck';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ENDPOINT = 'https://formspree.io/f/xgoldgzp';
const CMD      = 'node contact.js';

const HELP_TEXT =
`commands:
  reboot      restart to boot_main
  oraculus    launch oraculus
  contact     open contact form`;

const KNOWN_APPS = new Set(['boot_main', 'oraculus', 'contact', 'reboot']);

// uiPhase: 'form' | 'processing' | 'saved' | 'thankyou'

const ContactApp = React.forwardRef(function ContactApp(
  { onCommand, onStepChange },
  ref
) {
  // Boot
  const [cmdText,   setCmdText]   = useState('');
  const [showLine1, setShowLine1] = useState(false); // intro text
  const [showForm,  setShowForm]  = useState(false); // email prompt

  // Form
  const [step,         setStep]         = useState('email');
  const [email,        setEmail]        = useState('');
  const [savedMessage, setSavedMessage] = useState('');
  const [inputText,    setInputText]    = useState('');
  const [status,       setStatus]       = useState('');

  // Submission UI phase
  const [uiPhase, setUiPhase] = useState('form');

  // Terminal switching (hero context)
  const [showTermInput, setShowTermInput] = useState(false);
  const [termText,      setTermText]      = useState('');
  const [errorLine,     setErrorLine]     = useState('');
  const [helpLine,      setHelpLine]      = useState('');

  const formInputRef  = useRef(null);
  const termInputRef  = useRef(null);
  const errorTimerRef = useRef(null);
  const helpTimerRef  = useRef(null);

  useImperativeHandle(ref, () => ({
    focus() {
      if (step !== 'done') formInputRef.current?.focus({ preventScroll: true });
    },
    reset() {
      setStep('email');
      setEmail('');
      setSavedMessage('');
      setInputText('');
      setStatus('');
      setUiPhase('form');
      setCmdText(CMD);     // boot already ran — show cmd immediately
      setShowLine1(true);  // skip animation, restore form straight away
      setShowForm(true);
      setShowTermInput(false);
      setTermText('');
      setErrorLine('');
      setHelpLine('');
      setTimeout(() => formInputRef.current?.focus({ preventScroll: true }), 0);
    },
  }));

  useEffect(() => {
    onStepChange?.(step, status);
  }, [step, status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showForm && step === 'email') {
      setTimeout(() => formInputRef.current?.focus({ preventScroll: true }), 0);
    }
  }, [showForm]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step === 'message' && uiPhase === 'form') {
      setTimeout(() => formInputRef.current?.focus({ preventScroll: true }), 0);
    }
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showTermInput) termInputRef.current?.focus({ preventScroll: true });
  }, [showTermInput]);

  // Boot sequence
  useEffect(() => {
    const timers = [];
    const T = (fn, d) => { const id = setTimeout(fn, d); timers.push(id); return id; };
    let ci = 0;
    const cmdTick = () => {
      ci++;
      setCmdText(CMD.slice(0, ci));
      if (ci < CMD.length) T(cmdTick, 38);
      else {
        T(() => setShowLine1(true), 350); // intro text slides in
        T(() => setShowForm(true),  800); // email prompt slides in
      }
    };
    T(cmdTick, 300);
    return () => timers.forEach(clearTimeout);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Form handler ────────────────────────────────────────────────────────────
  const handleFormKey = async (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const val = inputText.trim();

    if (step === 'email') {
      if (!EMAIL_RE.test(val)) { setStatus('invalid_email'); return; }
      setEmail(val);
      setInputText('');
      setStatus('');
      setStep('message');
      return;
    }

    if (step === 'message') {
      if (val.length < 3) { setStatus('invalid_msg'); return; }
      const msg = val;
      setSavedMessage(msg);
      setInputText('');
      setStatus('');
      setStep('done');

      // Show processing inline
      setUiPhase('processing');

      // Submit + enforce minimum display time
      const results = await Promise.allSettled([
        fetch(ENDPOINT, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body:    JSON.stringify({ email, message: msg }),
        }),
        new Promise(r => setTimeout(r, 900)),
      ]);

      const fetchResult = results[0];
      const isOk = fetchResult.status === 'fulfilled' && fetchResult.value.ok;

      if (isOk) {
        setUiPhase('saved');
        setTimeout(() => {
          setUiPhase('thankyou');
          if (onCommand) setTimeout(() => setShowTermInput(true), 600);
        }, 4000);
      } else {
        setStep('message');
        setUiPhase('form');
        setStatus('error');
      }
    }
  };

  // ── Terminal switching (hero context) ───────────────────────────────────────
  const handleTermKey = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const raw = termText.trim();
    const cmd = raw.toLowerCase();
    if (cmd === '') { setTermText(''); return; }

    if (cmd === 'help') {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      if (helpTimerRef.current)  clearTimeout(helpTimerRef.current);
      setErrorLine('');
      setHelpLine(HELP_TEXT);
      setTermText('');
      helpTimerRef.current = setTimeout(() => setHelpLine(''), 12000);
    } else if (cmd === 'deck') {
      openDeckPage();
      setTermText('');
      setErrorLine('');
      setHelpLine('');
    } else if (KNOWN_APPS.has(cmd)) {
      onCommand(cmd);
      setTermText('');
      setErrorLine('');
      setHelpLine('');
    } else {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      if (helpTimerRef.current)  clearTimeout(helpTimerRef.current);
      setHelpLine('');
      setErrorLine(`-bash: ${raw}: command not found`);
      setTermText('');
      errorTimerRef.current = setTimeout(() => setErrorLine(''), 10000);
    }
  };

  const isEmailDone = step === 'message' || step === 'done';
  const isMsgDone   = step === 'done';

  return (
    <>
      {/* Boot cmd line — marginBottom overrides the 32px hero default */}
      <div
        className="hero-boot"
        style={{ marginBottom: '4px' }}
        onClick={() => step !== 'done' && formInputRef.current?.focus({ preventScroll: true })}
      >
        <p className="hero-boot-cmd">
          <span className="hero-boot-prompt">$</span>{' '}{cmdText}
          {cmdText.length < CMD.length && <span className="cursor hero-boot-cur">_</span>}
        </p>
      </div>

      {/* ── Single stable body — never removed from DOM ── */}
      <div
        className="hero-app-body"
        onClick={() => step !== 'done' && formInputRef.current?.focus({ preventScroll: true })}
      >
        {/* Line 1: intro — slides in first */}
        <p className={`hero-terminal-line contact-prog-dim boot-reveal${showLine1 ? ' boot-visible' : ''}`}>
          drop your email and we'll reach out.
        </p>

        {/* Email prompt — slides in second */}
        <div className={`hero-boot-cmd boot-reveal${showForm ? ' boot-visible' : ''}`}>
          <span className="hero-boot-prompt">$</span>
          <span className="hero-terminal-text">
            {isEmailDone ? email : inputText}
            {step === 'email' && <span className="cursor hero-boot-cur">_</span>}
          </span>
        </div>
        {step === 'email' && showForm && (
          <input
            ref={formInputRef}
            value={inputText}
            onChange={e => { setStatus(''); if (e.target.value.length <= 200) setInputText(e.target.value); }}
            onKeyDown={handleFormKey}
            className="hero-terminal-hidden"
            autoComplete="off" autoCorrect="off" spellCheck="false"
          />
        )}
        {status === 'invalid_email' && (
          <p className="hero-terminal-line contact-prog-err">invalid email format.</p>
        )}

        {/* Message prompt */}
        {isEmailDone && (<>
          <p className="hero-terminal-line contact-prog-dim">leave a message.</p>
          <div className="hero-boot-cmd">
            <span className="hero-boot-prompt">$</span>
            <span className="hero-terminal-text">
              {isMsgDone ? savedMessage : inputText}
              {step === 'message' && <span className="cursor hero-boot-cur">_</span>}
            </span>
          </div>
          {step === 'message' && (
            <input
              ref={formInputRef}
              value={inputText}
              onChange={e => { setStatus(''); if (e.target.value.length <= 500) setInputText(e.target.value); }}
              onKeyDown={handleFormKey}
              className="hero-terminal-hidden"
              autoComplete="off" autoCorrect="off" spellCheck="false"
            />
          )}
          {status === 'invalid_msg' && (
            <p className="hero-terminal-line contact-prog-err">message too short.</p>
          )}
          {status === 'error' && (
            <p className="hero-terminal-line contact-prog-err">something went wrong. try again.</p>
          )}
        </>)}

        {/* ── Submission output — appended as terminal lines ── */}
        {uiPhase === 'processing' && (
          <p className="contact-prog-processing">processing...</p>
        )}
        {(uiPhase === 'saved' || uiPhase === 'thankyou') && (
          <p className="contact-prog-saved">saved!</p>
        )}
        {uiPhase === 'thankyou' && (
          <p className="contact-prog-thankyou">we'll be in touch soon.</p>
        )}

        {/* Hint — hidden once submitted */}
        {onCommand && step !== 'done' && (
          <p className={`hero-terminal-line contact-prog-hint boot-reveal${showForm ? ' boot-visible' : ''}`}>
            ● close window to restart
          </p>
        )}
      </div>

      {/* Terminal switching input — hero context only */}
      {onCommand && (
        <div className={`hero-terminal-block boot-reveal${showTermInput ? ' boot-visible' : ''}`}>
          {helpLine  && <pre className="hero-terminal-help">{helpLine}</pre>}
          {!helpLine && errorLine && <p className="hero-terminal-error">{errorLine}</p>}
          <div className="hero-terminal-input" onClick={() => termInputRef.current?.focus({ preventScroll: true })}>
            <span className="hero-boot-prompt">$</span>
            <div className="hero-terminal-input-area">
              <span className="hero-terminal-text">{termText}</span>
              <span className="cursor hero-terminal-cur">_</span>
            </div>
            <input
              ref={termInputRef}
              value={termText}
              onChange={e => { if (e.target.value.length <= 300) setTermText(e.target.value); }}
              onKeyDown={handleTermKey}
              className="hero-terminal-hidden"
              autoComplete="off" autoCorrect="off" spellCheck="false"
            />
          </div>
        </div>
      )}
    </>
  );
});

export default ContactApp;
